import React, { useCallback, useEffect, useState } from 'react'
import { FaDownload, FaListUl, FaCamera } from 'react-icons/fa'
import { Stack, Heading, Text, Box, Card, Button, Icon, SimpleTable, SimpleModal, Badge, ConfirmButton } from '@/components/lib'
import api from '../../../api'
import useStore from '../../../store'
import utils from '@/utils/utils'

const TRIGGERS = {
  lock: 'Trial lock',
  databaseLock: 'Database lock',
  manual: 'On demand'
}

// The numbered snapshots of the trial's configuration: one is recorded
// automatically at each trial or database lock (unless nothing has changed
// since the last), and one can be recorded on demand — "configuration v2,
// approved for go-live". Each carries what changed since the one before.
export default function ConfigurationVersions () {
  const { user, trial } = useStore()
  const canManage = utils.canManageTrial(user, trial)
  const [versions, setVersions] = useState([])
  const [viewing, setViewing] = useState(null)
  const [message, setMessage] = useState(null)

  const load = useCallback(() => {
    api.trials.getConfigurationVersions(trial._id, ({ versions }) => setVersions(versions || []), () => setVersions([]))
  }, [trial._id])
  useEffect(() => { load() }, [load, trial.isLocked, trial.isDatabaseLocked])

  const record = (note) => {
    setMessage(null)
    api.trials.recordConfigurationVersion(trial._id, note, (version) => {
      if (version.unchanged) {
        setMessage(`Nothing has changed since version ${version.number}, so no new version was recorded.`)
      } else {
        setMessage(`Configuration version ${version.number} recorded.`)
        load()
      }
    }, (err) => setMessage(err?.message || 'Unable to record a version'))
  }

  const download = (number) => {
    api.trials.getConfigurationVersion(trial._id, number, null, (version) => {
      const blob = new Blob([JSON.stringify(version, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      utils.downloadFile(url, `${(trial.name || 'trial').replace(/ /g, '_')}_configuration_v${number}.json`)
      setTimeout(() => URL.revokeObjectURL(url), 4000)
    })
  }

  const view = (number) => {
    api.trials.getConfigurationVersion(trial._id, number, null, (version) => setViewing(version))
  }

  return (
    <Card variant='outline' gap={2}>
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
        <Box flex={1}>
          <Heading as='h3' size='md'>Configuration versions</Heading>
          <Text fontSize='sm' color='fg.muted'>
            Each time the trial is locked, Trialflare builds a versioned configuration snapshot. You can also manually trigger a snapshot build.
          </Text>
        </Box>
        {canManage &&
          <Box ml={4}>
            <ConfirmButton
              header='Record a configuration version'
              body='Trialflare will snapshot the configuration as it stands now and record it as the next version.'
              action='Record version'
              reasonRequired
              reasonLabel='Note'
              onConfirm={record}
              trigger={<Button size='sm'><Icon as={FaCamera} mr={1} /> Create a snapshot</Button>}
            />
          </Box>}
      </Stack>
      {message && <Text fontSize='sm'>{message}</Text>}
      {versions.length
        ? <SimpleTable
            size='sm'
            headers={['Version', 'Recorded', 'By', 'Trigger', 'Note', 'Changes since previous', '']}
            rows={versions.map(v => [
              <Badge key='n'>v{v.number}</Badge>,
              utils.formatDateTime(v.createdAt),
              v.createdByEmail,
              TRIGGERS[v.trigger] || v.trigger,
              v.note || '',
              v.previousNumber ? v.changeSummary?.text : 'first version',
              <Stack key='a' direction='row' gap={1}>
                {v.previousNumber && <Button size='xs' variant='outline' onClick={() => view(v.number)}><Icon as={FaListUl} mr={1} /> Changes</Button>}
                <Button size='xs' variant='outline' onClick={() => download(v.number)}><Icon as={FaDownload} mr={1} /> JSON</Button>
              </Stack>
            ])}
          />
        : <Text fontSize='sm' color='fg.subtle'>No configuration versions yet. One is recorded automatically when the trial or database is locked.</Text>}
      <SimpleModal
        size='xl'
        isOpen={!!viewing}
        onClose={() => setViewing(null)}
        header={viewing ? `Changes in configuration v${viewing.number} since v${viewing.previousNumber}` : ''}
        body={viewing && <Changes changes={viewing.changes} />}
      />
    </Card>
  )
}

function Changes ({ changes }) {
  if (!changes?.length) return <Text fontSize='sm' color='fg.subtle'>No changes.</Text>
  const show = (value) => value === undefined || value === null ? '—' : typeof value === 'object' ? JSON.stringify(value) : String(value)
  return (
    <Stack gap={3}>
      {changes.map((c, i) => (
        <Box key={i}>
          <Text fontSize='sm' fontWeight='semibold'>
            <Badge mr={2} colorPalette={c.kind === 'added' ? 'green' : c.kind === 'removed' ? 'red' : 'blue'}>{c.kind}</Badge>
            {c.collection === 'trial' ? 'Trial settings' : `${c.collection}: ${c.name}`}
          </Text>
          {c.fields?.length > 0 &&
            <SimpleTable
              size='sm'
              headers={['Field', 'Before', 'After']}
              rows={c.fields.map(f => [f.field, <Text key='b' fontSize='xs' fontFamily='mono' wordBreak='break-all'>{show(f.before)}</Text>, <Text key='a' fontSize='xs' fontFamily='mono' wordBreak='break-all'>{show(f.after)}</Text>])}
            />}
        </Box>
      ))}
    </Stack>
  )
}
